/**
 * Resolve warehouse inventory list id from WhsCode (store id) and persist cookies (IM-002).
 *
 * Runs only when:
 * - warehouseInventories site preference is non-empty (IM-001)
 * - Shipping API path was used (WhsCode from API; SFCC fallback has no warehouse code)
 * - whsCode and address are present
 */

import {useEffect, useRef} from 'react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {useResolvedInventoryListId} from './use-resolved-inventory-list-id';
import {
  sanitizeDeliveryAddressForCookie,
  setWarehouseInventoryCookies,
} from '../utils/warehouse-inventory-cookies';

/**
 * @param {Object} options
 * @param {string|undefined|null} options.whsCode - SFCC store id from Shipping API
 * @param {Object|undefined|null} options.address - shipment / formatted address
 * @param {boolean} [options.shippingApiActive] - true when response source is SHIPPING_API or HYBRID
 * @param {boolean} [options.enabled] - master switch (e.g. checkout step active)
 * @param {boolean} [options.persistCookies=true] - when false, resolves store/inventory but does not
 *   write `kl_wh_*` cookies (PDP uses this until the shopper explicitly changes delivery address).
 */
export function useWarehouseInventory({
  whsCode,
  address,
  shippingApiActive = false,
  enabled = true,
  persistCookies = true,
}) {
  const {site} = useMultiSite();
  const siteId = site?.id || 'kl-au';

  const shouldQuery = Boolean(enabled && shippingApiActive && whsCode);

  const {
    inventoryListId,
    isLoading,
    isError,
    isSuccess,
    store,
  } = useResolvedInventoryListId({
    whsCode,
    enabled: shouldQuery,
  });

  const lastWrittenKey = useRef('');

  useEffect(() => {
    if (
      !shouldQuery ||
      !persistCookies ||
      !isSuccess ||
      !inventoryListId
    ) {
      return;
    }

    const safeAddr = sanitizeDeliveryAddressForCookie(address);
    const key = `${inventoryListId}|${JSON.stringify(safeAddr || {})}`;

    if (lastWrittenKey.current === key) {
      return;
    }

    lastWrittenKey.current = key;
    setWarehouseInventoryCookies(siteId, inventoryListId, safeAddr);
  }, [address, inventoryListId, isSuccess, persistCookies, shouldQuery, siteId]);

  return {
    isLoading: shouldQuery && isLoading,
    isError,
    inventoryListId,
    store,
  };
}

export default useWarehouseInventory;
